import type { Copy } from '../../types/copy'

export const esCopy: Copy = {
  language: 'es',
  badge: 'Programa Mente Valiente',
  access: {
    headline: 'Tu experiencia personalizada comienza aquí 💜',
    subtext: 'Ingresa tus datos para comenzar a crear el programa de tu hijo o hija.',
    nameLabel: 'Tu nombre',
    namePlaceholder: 'Laura',
    emailLabel: 'Correo electrónico utilizado en la compra',
    emailPlaceholder: 'laura@correo.com',
    submit: 'Acceder a mi programa →',
    footnote: 'Esto tomará solo unos minutos.',
    errorName: 'Escribe tu nombre para continuar.',
    errorEmail: 'Revisa el correo que usaste en tu compra.',
  },
  preparing: {
    loading: 'Preparando tu experiencia…',
    ready: 'Todo listo 💜',
  },
  welcome: {
    greeting: (parentName) => `Bienvenida, ${parentName} 💜`,
    fallbackName: 'familia',
    headline: 'Ahora vamos a crear algo único para tu pequeño.',
    subtext:
      'Necesitamos conocer un poco mejor a tu hijo o hija para personalizar sus 5 experiencias de audio.',
    highlights: [
      { emoji: '⏱️', text: 'Toma aproximadamente 5 minutos' },
      { emoji: '🎧', text: '5 experiencias personalizadas' },
      { emoji: '✨', text: 'Nosotros haremos el resto' },
      { emoji: '💜', text: 'Recibirás todo por correo' },
    ],
    cta: 'Comenzar la personalización →',
  },
  quiz: {
    stepOf: (step, total) => `Paso ${step} de ${total}`,
    backLabel: 'Volver a la pregunta anterior',
    continue: 'Continuar →',
    seeSummary: 'Ver resumen →',
  },
  steps: {
    name: {
      headline: '¿Cómo se llama tu pequeño? 💜',
      subtext: 'Usaremos su nombre dentro de las experiencias de audio.',
      label: 'Nombre o apodo',
      placeholder: 'Martina',
      genderLegend: '¿Es hijo o hija?',
      son: 'Hijo',
      daughter: 'Hija',
    },
    age: {
      headline: (childName) => `¿Cuántos años tiene ${childName}?`,
      subtext: 'Así ajustamos el lenguaje y el tono de sus audios.',
    },
    interests: {
      headline: (childName) => `¿Qué cosas le encantan a ${childName}? ✨`,
      subtext: 'Puedes elegir varias opciones.',
      otherLabel: 'Cuéntanos qué le encanta',
      otherPlaceholder: 'Le fascinan los trenes antiguos',
    },
    personality: {
      headline: (childName) => `¿Cómo describirías a ${childName}?`,
      subtext: (max) => `Puedes elegir hasta ${max}.`,
    },
    priorities: {
      headline: (childName) => `¿Qué te gustaría ayudar a ${childName} a fortalecer?`,
      subtext: (max) => `Selecciona hasta ${max} opciones.`,
    },
    context: {
      headline: 'Cuéntanos un poquito más 💜',
      question: '¿En qué momento te gustaría que estas experiencias lo acompañaran?',
      contextLabel: 'Si quieres, cuéntanos una situación breve. (opcional)',
      contextPlaceholder:
        'Por ejemplo: cuando algo no le sale a la primera, se frustra y dice que no puede.',
    },
    personal: {
      headline: 'Ahora viene la parte más especial ✨',
      question: (childName) =>
        `¿Hay algo único de ${childName} que quieras que aparezca en sus experiencias?`,
      detailPlaceholder:
        'Por ejemplo: le encanta construir cosas con su abuelo, sueña con viajar al espacio o está aprendiendo a andar en bicicleta.',
      messageLabel: '¿Qué mensaje te gustaría que recordara? (opcional)',
      messagePlaceholder:
        'Quiero que recuerde que confiamos en él y que puede lograr cosas grandes.',
    },
  },
  summary: {
    headline: (childName) => `Conocimos un poquito mejor a ${childName} 💜`,
    genderRow: 'Hijo o hija',
    ageRow: 'Edad',
    interestsRow: 'Intereses',
    personalityRow: 'Personalidad',
    prioritiesRow: 'Fortalezas a trabajar',
    ageValue: (age) => `${age} años`,
    note: (childName) =>
      `Con esta información podremos crear experiencias que se sientan realmente hechas para ${childName}.`,
    consent:
      'Confirmo que soy padre, madre o tutor responsable y autorizo el uso de esta información para crear el producto personalizado que compré.',
    submit: (childName) => `Crear el programa de ${childName} ✨`,
    sending: 'Enviando…',
    edit: 'Modificar respuestas',
    networkError: 'No pudimos enviar el perfil. Revisa tu conexión e intenta de nuevo.',
  },
  processing: {
    first: (childName) => `Preparando el perfil de ${childName}…`,
    second: 'Organizando su información…',
    third: 'Todo listo 💜',
  },
  final: {
    badge: 'Perfil recibido',
    headline: 'Ahora comienza la magia ✨',
    subtext: (childName) =>
      `Ya tenemos todo lo necesario para crear las 5 experiencias personalizadas de ${childName}.`,
    timeline: [
      'Perfil recibido',
      'Creación personalizada',
      'Preparación de audios',
      'Revisión',
      'Entrega por correo',
    ],
    emailNote: 'Recibirás todo en el correo que utilizaste para acceder',
    eta: 'Tiempo estimado: 3 a 5 días hábiles.',
    card: 'Ya no necesitas hacer nada más.\nNosotros nos encargamos del resto 💜',
    cta: 'Finalizar',
  },
  closed: {
    heading: (parentName) => `Gracias, ${parentName}`,
    fallbackName: 'de corazón',
    text: (childName, email) =>
      `Ya puedes cerrar esta ventana. Te escribiremos a ${email} cuando las experiencias de ${childName} estén listas.`,
  },
  options: {
    interests: {
      dinosaurs: 'Dinosaurios',
      robots: 'Robots',
      space: 'Espacio',
      vehicles: 'Vehículos',
      animals: 'Animales',
      drawing: 'Dibujar',
      sports: 'Deportes',
      nature: 'Naturaleza',
      puzzles: 'Rompecabezas',
      stories: 'Historias',
      fantasy: 'Fantasía',
      music: 'Música',
      princesses: 'Princesas',
      fairies: 'Hadas',
      other: 'Algo diferente',
    },
    personality: {
      curious: 'Muy curioso/a',
      imaginative: 'Imaginativo/a',
      sensitive: 'Sensible',
      energetic: 'Lleno/a de energía',
      calm: 'Tranquilo/a',
      social: 'Sociable',
      brave: 'Valiente',
      shy: 'Tímido/a al principio',
      determined: 'Determinado/a',
      thoughtful: 'Reflexivo/a',
      independent: 'Independiente',
      affectionate: 'Cariñoso/a',
    },
    priorities: {
      confidence: { title: 'Confianza', description: 'Creer más en sí mismo.' },
      calm: { title: 'Calma', description: 'Recuperar la tranquilidad cuando algo lo frustra.' },
      perseverance: {
        title: 'Perseverancia',
        description: 'Volver a intentarlo cuando algo parece difícil.',
      },
      focus: { title: 'Enfoque', description: 'Concentrarse mejor y terminar lo que comienza.' },
      creativity: {
        title: 'Creatividad',
        description: 'Confiar en sus ideas y crear nuevas soluciones.',
      },
    },
    situations: {
      not_working: 'Cuando algo no le sale',
      frustrated: 'Cuando se frustra',
      afraid_to_try: 'Cuando tiene miedo de intentarlo',
      learning_new: 'Cuando aprende algo nuevo',
      loses_focus: 'Cuando pierde la concentración',
      gives_up: 'Cuando quiere abandonar rápido',
      self_doubt: 'Cuando duda de sí mismo',
      none: 'Ninguna en particular',
    },
  },
}
